'use client';

import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Search, 
  HelpCircle, 
  BookOpen, 
  BarChart3, 
  TrendingUp, 
  Settings, 
  Menu,
  ExternalLink
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { 
  helpArticles, 
  getHelpArticlesByCategory, 
  searchHelpArticles,
  type HelpCategory,
  type HelpArticle
} from '@/lib/help-content';
import { logger } from '@/lib/logger';

interface HelpCenterProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialArticleId?: string;
}

const categoryIcons: Record<HelpCategory, React.ComponentType<{ className?: string }>> = {
  'getting-started': BookOpen,
  'scenarios': BarChart3,
  'projections': TrendingUp,
  'settings': Settings,
  'navigation': Menu,
  'ui-elements': HelpCircle,
};

const categoryLabels: Record<HelpCategory, string> = {
  'getting-started': 'Getting Started',
  'scenarios': 'Scenarios',
  'projections': 'Projections',
  'settings': 'Settings',
  'navigation': 'Navigation',
  'ui-elements': 'UI Elements',
};

export function HelpCenter({ open, onOpenChange, initialArticleId }: HelpCenterProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedArticle, setSelectedArticle] = useState<HelpArticle | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<HelpCategory | 'all'>('all');

  // Handle initial article selection when opened with article ID
  React.useEffect(() => {
    if (open && initialArticleId) {
      const article = helpArticles.find(a => a.id === initialArticleId);
      if (article) {
        setSelectedArticle(article);
        setSelectedCategory(article.category);
        setSearchQuery(''); // Clear search when opening specific article
      }
    } else if (open && !initialArticleId) {
      // Reset selection when opening without article ID
      setSelectedArticle(null);
    }
  }, [open, initialArticleId]);

  // Track help center opens
  React.useEffect(() => {
    if (open) {
      logger.info('Help center opened');
    }
  }, [open]);

  const filteredArticles = useMemo(() => {
    if (searchQuery.trim()) {
      return searchHelpArticles(searchQuery);
    }
    
    if (selectedCategory === 'all') {
      return helpArticles;
    }
    
    return getHelpArticlesByCategory(selectedCategory);
  }, [searchQuery, selectedCategory]);

  const handleArticleClick = (article: HelpArticle) => {
    setSelectedArticle(article);
    logger.info('Help article viewed', { articleId: article.id, title: article.title });
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    logger.info('Help center search', { query });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[80vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <div className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5" />
            <DialogTitle>Help Center</DialogTitle>
          </div>
        </DialogHeader>

        <div className="flex-1 flex overflow-hidden">
          {/* Sidebar */}
          <div className="w-64 border-r flex flex-col">
            {/* Search */}
            <div className="p-4 border-b">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search help..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>

            {/* Categories */}
            <ScrollArea className="flex-1">
              <div className="p-4 space-y-2">
                <Button
                  variant={selectedCategory === 'all' ? 'secondary' : 'ghost'}
                  className="w-full justify-start"
                  onClick={() => {
                    setSelectedCategory('all');
                    setSelectedArticle(null);
                  }}
                >
                  <HelpCircle className="mr-2 h-4 w-4" />
                  All Topics
                </Button>
                
                {(['getting-started', 'navigation', 'ui-elements', 'scenarios', 'projections', 'settings'] as HelpCategory[]).map((category) => {
                  const Icon = categoryIcons[category];
                  return (
                    <Button
                      key={category}
                      variant={selectedCategory === category ? 'secondary' : 'ghost'}
                      className="w-full justify-start"
                      onClick={() => {
                        setSelectedCategory(category);
                        setSelectedArticle(null);
                      }}
                    >
                      <Icon className="mr-2 h-4 w-4" />
                      {categoryLabels[category]}
                    </Button>
                  );
                })}
              </div>
            </ScrollArea>

            {/* External Links */}
            <div className="p-4 border-t">
              <div className="text-xs font-semibold text-muted-foreground mb-2">External Resources</div>
              <div className="space-y-1">
                <a
                  href="https://github.com/devtechsystems/dt-debtcast/wiki"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ExternalLink className="h-3 w-3" />
                  GitHub Wiki
                </a>
                <a
                  href="/docs"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ExternalLink className="h-3 w-3" />
                  API Documentation
                </a>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {selectedArticle ? (
              // Article View
              <ScrollArea className="flex-1">
                <div className="p-6">
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="secondary">{categoryLabels[selectedArticle.category]}</Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedArticle(null)}
                      >
                        ← Back to Articles
                      </Button>
                    </div>
                    <h2 className="text-2xl font-bold">{selectedArticle.title}</h2>
                  </div>
                  
                  <div className="prose prose-lg max-w-none dark:prose-invert 
                    prose-headings:font-semibold prose-headings:text-foreground
                    prose-h1:text-3xl prose-h1:mt-8 prose-h1:mb-4
                    prose-h2:text-2xl prose-h2:mt-6 prose-h2:mb-3 prose-h2:border-b prose-h2:border-border prose-h2:pb-2
                    prose-h3:text-xl prose-h3:mt-5 prose-h3:mb-2
                    prose-h4:text-lg prose-h4:mt-4 prose-h4:mb-2
                    prose-p:text-foreground prose-p:leading-7 prose-p:my-4
                    prose-a:text-primary prose-a:font-medium prose-a:no-underline hover:prose-a:underline
                    prose-strong:text-foreground prose-strong:font-semibold
                    prose-ul:my-4 prose-ul:space-y-2 prose-li:my-1
                    prose-ol:my-4 prose-ol:space-y-2
                    prose-code:text-sm prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:font-mono
                    prose-pre:bg-muted prose-pre:border prose-pre:border-border prose-pre:rounded-lg prose-pre:p-4 prose-pre:overflow-x-auto
                    prose-blockquote:border-l-4 prose-blockquote:border-primary prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-muted-foreground
                    prose-hr:border-border prose-hr:my-6
                    prose-table:w-full prose-table:my-4
                    prose-th:bg-muted prose-th:font-semibold prose-th:p-2 prose-th:border prose-th:border-border
                    prose-td:p-2 prose-td:border prose-td:border-border">
                    <ReactMarkdown
                      components={{
                        h1: ({ children }) => <h1 className="text-3xl font-semibold mt-8 mb-4 text-foreground">{children}</h1>,
                        h2: ({ children }) => <h2 className="text-2xl font-semibold mt-6 mb-3 pb-2 border-b border-border text-foreground">{children}</h2>,
                        h3: ({ children }) => <h3 className="text-xl font-semibold mt-5 mb-2 text-foreground">{children}</h3>,
                        h4: ({ children }) => <h4 className="text-lg font-semibold mt-4 mb-2 text-foreground">{children}</h4>,
                        p: ({ children }) => <p className="text-foreground leading-7 my-4">{children}</p>,
                        a: ({ href, children }) => (
                          <a href={href} className="text-primary font-medium no-underline hover:underline transition-colors">
                            {children}
                          </a>
                        ),
                        ul: ({ children }) => <ul className="my-4 space-y-2 list-disc list-inside">{children}</ul>,
                        ol: ({ children }) => <ol className="my-4 space-y-2 list-decimal list-inside">{children}</ol>,
                        li: ({ children }) => <li className="my-1 text-foreground">{children}</li>,
                        code: ({ children, className }) => {
                          const isInline = !className;
                          return isInline ? (
                            <code className="text-sm bg-muted px-1.5 py-0.5 rounded font-mono text-foreground">
                              {children}
                            </code>
                          ) : (
                            <code className={className}>{children}</code>
                          );
                        },
                        pre: ({ children }) => (
                          <pre className="bg-muted border border-border rounded-lg p-4 overflow-x-auto my-4">
                            {children}
                          </pre>
                        ),
                        blockquote: ({ children }) => (
                          <blockquote className="border-l-4 border-primary pl-4 italic text-muted-foreground my-4">
                            {children}
                          </blockquote>
                        ),
                        hr: () => <hr className="border-border my-6" />,
                        table: ({ children }) => (
                          <div className="my-4 overflow-x-auto">
                            <table className="w-full border-collapse border border-border">
                              {children}
                            </table>
                          </div>
                        ),
                        th: ({ children }) => (
                          <th className="bg-muted font-semibold p-2 border border-border text-left">
                            {children}
                          </th>
                        ),
                        td: ({ children }) => (
                          <td className="p-2 border border-border">
                            {children}
                          </td>
                        ),
                      }}
                    >
                      {selectedArticle.content}
                    </ReactMarkdown>
                  </div>

                  {selectedArticle.relatedArticles.length > 0 && (
                    <div className="mt-8 pt-6 border-t">
                      <h3 className="text-sm font-semibold mb-3">Related Articles</h3>
                      <div className="space-y-2">
                        {selectedArticle.relatedArticles.map((id) => {
                          const article = helpArticles.find(a => a.id === id);
                          if (!article) return null;
                          return (
                            <Button
                              key={id}
                              variant="outline"
                              className="w-full justify-start"
                              onClick={() => handleArticleClick(article)}
                            >
                              {article.title}
                            </Button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </ScrollArea>
            ) : (
              // Article List
              <ScrollArea className="flex-1">
                <div className="p-6">
                  {filteredArticles.length === 0 ? (
                    <div className="text-center py-12">
                      <HelpCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">No articles found. Try a different search.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {filteredArticles.map((article) => {
                        const Icon = categoryIcons[article.category];
                        return (
                          <Card
                            key={article.id}
                            className="cursor-pointer hover:bg-accent transition-colors"
                            onClick={() => handleArticleClick(article)}
                          >
                            <CardHeader>
                              <div className="flex items-start justify-between">
                                <div className="flex items-center gap-2">
                                  <Icon className="h-4 w-4 text-muted-foreground" />
                                  <CardTitle className="text-lg">{article.title}</CardTitle>
                                </div>
                                <Badge variant="secondary">{categoryLabels[article.category]}</Badge>
                              </div>
                            </CardHeader>
                            <CardContent>
                              <CardDescription className="line-clamp-2">
                                {article.content.split('\n')[0]}
                              </CardDescription>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </div>
              </ScrollArea>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

